// src/components/UserSettings.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  styled,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Divider,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  Save,
  Business,
  Settings as SettingsIcon,
  Language,
  AccessTime,
  CalendarToday,
  Timer,
  Palette,
  Security,
  CheckCircle,
  Lock,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useThemeMode } from '../contexts/ThemeModeContext';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n/config';
import api from '../services/api';

const teslaColors = {
  primary: '#3E6AE1',
  secondary: '#34C3FF',
};

const GlassCard = styled(Card)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#1E1E1E' : '#FFFFFF',
  borderRadius: '16px',
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.palette.mode === 'dark'
    ? '0 2px 8px rgba(0, 0, 0, 0.25)'
    : '0 2px 8px rgba(0, 0, 0, 0.05)',
  backgroundImage: 'none',
}));

function UserSettings({ variant, isDark: propIsDark }) {
  const { user } = useAuth();
  const { language, setLanguage: setContextLanguage } = useLanguage();
  const { mode, setMode: setSharedThemeMode } = useThemeMode();
  const isDark = propIsDark !== undefined ? propIsDark : mode === 'dark';
  const { t } = useTranslation();

  const isPacs = variant === 'pacs' || (typeof window !== 'undefined' && (window.location.pathname.includes('radiologist') || window.location.pathname.includes('pacs')));
  const colors = isPacs ? {
    primary: '#3E6AE1',
    secondary: '#34C3FF',
    iconBg: isDark ? 'rgba(62, 106, 225, 0.2)' : 'rgba(62, 106, 225, 0.12)',
    iconColor: '#3E6AE1',
    btnBg: '#3E6AE1',
    btnHoverBg: '#2E5AD1',
    btnRadius: '8px',
    hoverBorder: '#3E6AE1',
    linkColor: '#3E6AE1',
  } : {
    primary: '#00665C',
    secondary: '#0D9488',
    iconBg: isDark ? 'rgba(13, 148, 136, 0.2)' : 'rgba(13, 148, 136, 0.12)',
    iconColor: '#0D9488',
    btnBg: '#00665C',
    btnHoverBg: '#004D47',
    btnRadius: '8px',
    hoverBorder: '#0D9488',
    linkColor: '#0D9488',
  };

  const cardBg = isDark ? '#1E1E1E' : '#FFFFFF';
  const cardBorder = `1.5px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : '#E2E8F0'}`;
  const textColorPrimary = isDark ? '#F1F5F9' : '#0F172A';
  const textColorSecondary = isDark ? '#CBD5E1' : '#475569';
  const borderColor = isDark ? 'rgba(255, 255, 255, 0.08)' : '#E2E8F0';

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [facilityInfo, setFacilityInfo] = useState({
    facilityName: '',
    facilityId: '',
    contactEmail: '',
  });

  const [accountSettings, setAccountSettings] = useState({
    defaultLanguage: 'en',
    timeZone: 'UTC',
    dateFormat: 'MM/DD/YYYY',
    autoLogoutTimer: 30,
    theme: 'light',
  });

  // MFA state
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [mfaSetupStep, setMfaSetupStep] = useState('idle'); // idle | setup | verify | done
  const [mfaQrCode, setMfaQrCode] = useState('');
  const [mfaSecret, setMfaSecret] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaLoading, setMfaLoading] = useState(false);
  const [mfaError, setMfaError] = useState('');
  const [mfaSuccess, setMfaSuccess] = useState('');

  useEffect(() => {
    loadSettings();
    loadFacilityInfo();
    detectTimezone();
    loadMfaStatus();
  }, []);

  const detectTimezone = () => {
    try {
      // Get timezone from browser
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (timezone && !accountSettings.timeZone) {
        setAccountSettings(prev => ({
          ...prev,
          timeZone: timezone,
        }));
      }
    } catch (error) {
      console.error('Error detecting timezone:', error);
    }
  };

  const loadSettings = async () => {
    try {
      const response = await api.get('/users/settings');
      if (response.data && response.data.success !== false) {
        const prefs = response.data.preferences || response.data;
        const userLanguage = prefs?.language || language || 'en';
        
        // Detect timezone if not set
        let timeZone = prefs?.timezone;
        if (!timeZone) {
          try {
            timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
          } catch (e) {
            timeZone = 'UTC';
          }
        }
        
        setAccountSettings({
          defaultLanguage: userLanguage,
          timeZone: timeZone,
          dateFormat: response.data.dateFormat || 'MM/DD/YYYY',
          autoLogoutTimer: response.data.autoLogoutTimer || 30,
          theme: prefs?.theme || 'light',
        });
        // Update context language if different
        if (userLanguage !== language) {
          setContextLanguage(userLanguage);
        }
      }
    } catch (err) {
      console.error('Error loading settings:', err);
      // Use context language and detected timezone as fallback
      let detectedTimezone = 'UTC';
      try {
        detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      } catch (e) {
        // Keep UTC
      }
      
      setAccountSettings(prev => ({
        ...prev,
        defaultLanguage: language || 'en',
        timeZone: detectedTimezone,
      }));
    }
  };

  const loadFacilityInfo = async () => {
    try {
      // Check if facility is already populated as an object
      if (user.facility && typeof user.facility === 'object' && user.facility.facilityName) {
        setFacilityInfo({
          facilityName: user.facility.facilityName || '',
          facilityId: user.facility._id || user.facility.facilityId || '',
          contactEmail: user.facility.email || user.facility.contactPerson?.email || '',
        });
        return;
      }
      
      // If facility is an ObjectId (string), fetch it
      const facilityId = user.facility?._id || user.facility;
      if (facilityId) {
        const response = await api.get(`/facilities/${facilityId}`);
        if (response.data && response.data.facility) {
          const facility = response.data.facility;
          setFacilityInfo({
            facilityName: facility.facilityName || '',
            facilityId: facility._id || facility.facilityId || '',
            contactEmail: facility.email || facility.contactPerson?.email || '',
          });
        } else if (response.data) {
          // Handle direct facility object in response
          setFacilityInfo({
            facilityName: response.data.facilityName || '',
            facilityId: response.data._id || response.data.facilityId || '',
            contactEmail: response.data.email || response.data.contactPerson?.email || '',
          });
        }
      } else if (user.facilityName) {
        // Fallback to direct facility properties on user object
        setFacilityInfo({
          facilityName: user.facilityName || '',
          facilityId: user.facilityId || '',
          contactEmail: user.facilityContactEmail || '',
        });
      }
    } catch (err) {
      console.error('Error loading facility info:', err);
      // If error, check if facility info is directly on user object
      if (user.facility && typeof user.facility === 'object') {
        setFacilityInfo({
          facilityName: user.facility.facilityName || 'N/A',
          facilityId: user.facility._id || user.facility.facilityId || '',
          contactEmail: user.facility.email || '',
        });
      }
    }
  };

  const loadMfaStatus = async () => {
    try {
      const res = await api.get('/auth/mfa/status');
      setMfaEnabled(res.data.mfaEnabled);
    } catch (_) {}
  };

  const handleMfaSetup = async () => {
    setMfaLoading(true);
    setMfaError('');
    setMfaSuccess('');
    try {
      const res = await api.post('/auth/mfa/setup');
      setMfaQrCode(res.data.qrCode);
      setMfaSecret(res.data.secret);
      setMfaSetupStep('verify');
    } catch (err) {
      setMfaError(err.response?.data?.error || 'Failed to start MFA setup');
    } finally {
      setMfaLoading(false);
    }
  };

  const handleMfaVerifySetup = async () => {
    setMfaLoading(true);
    setMfaError('');
    try {
      await api.post('/auth/mfa/verify-setup', { code: mfaCode });
      setMfaEnabled(true);
      setMfaSetupStep('idle');
      setMfaQrCode('');
      setMfaSecret('');
      setMfaCode('');
      setMfaSuccess('Two-factor authentication enabled successfully!');
    } catch (err) {
      setMfaError(err.response?.data?.error || 'Invalid code — try again');
    } finally {
      setMfaLoading(false);
    }
  };

  const handleMfaDisable = async () => {
    setMfaLoading(true);
    setMfaError('');
    try {
      await api.post('/auth/mfa/disable', { code: mfaCode });
      setMfaEnabled(false);
      setMfaCode('');
      setMfaSetupStep('idle');
      setMfaSuccess('Two-factor authentication has been disabled.');
    } catch (err) {
      setMfaError(err.response?.data?.error || 'Invalid code — try again');
    } finally {
      setMfaLoading(false);
    }
  };

  const handleAccountSettingChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setAccountSettings({
      ...accountSettings,
      [field]: value,
    });
  };

  const handleSave = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await api.patch('/users/settings', {
        preferences: {
          language: accountSettings.defaultLanguage,
          timezone: accountSettings.timeZone,
          theme: accountSettings.theme,
        },
        dateFormat: accountSettings.dateFormat,
        autoLogoutTimer: accountSettings.autoLogoutTimer,
      });
      
      console.log('Settings save response:', response.data);
      console.log('Changing language to:', accountSettings.defaultLanguage);
      
      // Always update language context - this will handle everything
      await setContextLanguage(accountSettings.defaultLanguage);
      
      console.log('Language context updated, current i18n language:', i18n.language);
      
      // Force a small delay to ensure everything is updated
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Verify the language was actually changed
      if (i18n.language !== accountSettings.defaultLanguage) {
        console.warn('Language mismatch! Expected:', accountSettings.defaultLanguage, 'Got:', i18n.language);
        // Try one more time
        await i18n.changeLanguage(accountSettings.defaultLanguage);
      }
      
      // Update user object in localStorage if response includes updated user
      if (response.data?.user) {
        localStorage.setItem('user', JSON.stringify(response.data.user));
      } else {
        // Update user preferences in stored user object
        try {
          const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
          if (storedUser && Object.keys(storedUser).length > 0) {
            storedUser.preferences = {
              ...storedUser.preferences,
              language: accountSettings.defaultLanguage,
              timezone: accountSettings.timeZone,
              theme: accountSettings.theme,
            };
            storedUser.dateFormat = accountSettings.dateFormat;
            storedUser.autoLogoutTimer = accountSettings.autoLogoutTimer;
            localStorage.setItem('user', JSON.stringify(storedUser));
          }
        } catch (e) {
          console.error('Error updating stored user:', e);
        }
      }
      
      // Apply immediately via the shared theme context (persists to localStorage itself)
      const themeValue = accountSettings.theme || (response.data?.preferences?.theme || response.data?.user?.preferences?.theme || 'light');
      setSharedThemeMode(themeValue);

      // Dispatch custom event to notify other open tabs/components about the theme change
      window.dispatchEvent(new CustomEvent('themeChanged', { detail: { theme: themeValue } }));
      
      // Reload settings to ensure UI reflects changes
      await loadSettings();
      
      setSuccess('Settings saved successfully');
      
      // Trigger a custom event to notify other components (like Dashboard) to reload settings
      window.dispatchEvent(new CustomEvent('userSettingsUpdated', { 
        detail: { 
          preferences: response.data?.preferences || response.data?.user?.preferences,
          dateFormat: response.data?.dateFormat,
          autoLogoutTimer: response.data?.autoLogoutTimer
        } 
      }));
    } catch (err) {
      console.error('Error saving settings:', err);
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const languages = [
    { value: 'en', label: 'English' },
    { value: 'es', label: 'Spanish' },
    { value: 'fr', label: 'French' },
    { value: 'de', label: 'German' },
    { value: 'zh', label: 'Chinese' },
    // African Languages
    { value: 'sw', label: 'Swahili (Kiswahili)' },
    { value: 'am', label: 'Amharic (አማርኛ)' },
    { value: 'ha', label: 'Hausa' },
    { value: 'yo', label: 'Yoruba' },
    { value: 'ig', label: 'Igbo' },
    { value: 'zu', label: 'Zulu (isiZulu)' },
    { value: 'xh', label: 'Xhosa (isiXhosa)' },
    { value: 'af', label: 'Afrikaans' },
    { value: 'ar', label: 'Arabic (العربية)' },
    { value: 'so', label: 'Somali (Soomaali)' },
    { value: 'rw', label: 'Kinyarwanda' },
    { value: 'ak', label: 'Akan' },
    { value: 'ff', label: 'Fulfulde' },
    { value: 'ln', label: 'Lingala' },
    { value: 'mg', label: 'Malagasy' },
    { value: 'om', label: 'Oromo (Afaan Oromoo)' },
    { value: 'ti', label: 'Tigrinya (ትግርኛ)' },
    { value: 'tn', label: 'Tswana (Setswana)' },
    { value: 've', label: 'Venda (Tshivenda)' },
    { value: 'wo', label: 'Wolof' },
  ];

  const timeZones = [
    { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'Europe/London', label: 'London (GMT)' },
    { value: 'Europe/Paris', label: 'Paris (CET)' },
    { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
    { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
    { value: 'Australia/Sydney', label: 'Sydney (AEST)' },
  ];

  const dateFormats = [
    { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
    { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
    { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' },
    { value: 'DD-MM-YYYY', label: 'DD-MM-YYYY' },
  ];

  return (
    <Box sx={{ maxWidth: 960 }}>
      {/* Header */}
      <Box mb={1.5}>
        <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.1rem', lineHeight: 1.2, fontFamily: '"Satoshi", sans-serif', color: textColorPrimary }}>
          Settings
        </Typography>
        <Typography variant="caption" sx={{ fontSize: '0.72rem', fontFamily: '"Satoshi", sans-serif', color: textColorSecondary }}>
          View and configure system-level and personal account settings
        </Typography>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 1.5, py: 0.25, borderRadius: '6px', fontSize: '0.75rem', fontFamily: '"Satoshi", sans-serif' }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 1.5, py: 0.25, borderRadius: '6px', fontSize: '0.75rem', fontFamily: '"Satoshi", sans-serif' }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Grid container spacing={1.5}>
        {/* Facility Information Section */}
        <Grid item xs={12}>
          <Card
            sx={{
              background: cardBg,
              borderRadius: '12px',
              border: cardBorder,
              boxShadow: 'none',
            }}
          >
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box display="flex" alignItems="center" gap={1} mb={0.75}>
                <Box
                  sx={{
                    width: 26,
                    height: 26,
                    borderRadius: '6px',
                    bgcolor: colors.iconBg,
                    color: colors.iconColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Business sx={{ fontSize: 16 }} />
                </Box>
                <Typography variant="subtitle2" sx={{ fontFamily: '"Satoshi", sans-serif', fontWeight: 700, fontSize: '0.88rem', color: textColorPrimary }}>
                  {t('settings.facilityInfo')}
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ fontFamily: '"Satoshi", sans-serif', fontSize: '0.72rem', color: textColorSecondary, display: 'block', mb: 1.5 }}>
                {t('settings.readOnlyNote')}
              </Typography>

              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t('settings.facilityName')}
                    value={facilityInfo.facilityName}
                    disabled
                    InputProps={{ sx: { fontFamily: '"Satoshi", sans-serif', fontSize: '0.8rem' } }}
                    InputLabelProps={{ sx: { fontFamily: '"Satoshi", sans-serif', fontSize: '0.8rem' } }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t('settings.facilityId')}
                    value={facilityInfo.facilityId}
                    disabled
                    InputProps={{ sx: { fontFamily: '"Satoshi", sans-serif', fontSize: '0.8rem' } }}
                    InputLabelProps={{ sx: { fontFamily: '"Satoshi", sans-serif', fontSize: '0.8rem' } }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t('settings.contactEmail')}
                    value={facilityInfo.contactEmail}
                    disabled
                    InputProps={{ sx: { fontFamily: '"Satoshi", sans-serif', fontSize: '0.8rem' } }}
                    InputLabelProps={{ sx: { fontFamily: '"Satoshi", sans-serif', fontSize: '0.8rem' } }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Personal / Account Settings Section */}
        <Grid item xs={12}>
          <Card
            sx={{
              background: cardBg,
              borderRadius: '12px',
              border: cardBorder,
              boxShadow: 'none',
            }}
          >
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box display="flex" alignItems="center" gap={1} mb={0.75}>
                <Box
                  sx={{
                    width: 26,
                    height: 26,
                    borderRadius: '6px',
                    bgcolor: colors.iconBg,
                    color: colors.iconColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <SettingsIcon sx={{ fontSize: 16 }} />
                </Box>
                <Typography variant="subtitle2" sx={{ fontFamily: '"Satoshi", sans-serif', fontWeight: 700, fontSize: '0.88rem', color: textColorPrimary }}>
                  {t('settings.accountSettings')}
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ fontFamily: '"Satoshi", sans-serif', fontSize: '0.72rem', color: textColorSecondary, display: 'block', mb: 1.5 }}>
                Configure your account preferences and system experience
              </Typography>

              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel sx={{ fontFamily: '"Satoshi", sans-serif', fontSize: '0.8rem' }}>{t('settings.defaultLanguage')}</InputLabel>
                    <Select
                      value={accountSettings.defaultLanguage}
                      onChange={handleAccountSettingChange('defaultLanguage')}
                      label={t('settings.defaultLanguage')}
                      startAdornment={<Language sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />}
                      sx={{ borderRadius: '8px', fontFamily: '"Satoshi", sans-serif', fontSize: '0.8rem' }}
                    >
                      {languages.map((lang) => (
                        <MenuItem key={lang.value} value={lang.value} sx={{ fontFamily: '"Satoshi", sans-serif', fontSize: '0.8rem' }}>
                          {lang.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Typography 
                    variant="caption" 
                    color="textSecondary" 
                    sx={{ mt: 0.5, display: 'block', fontStyle: 'italic', fontSize: '0.68rem', fontFamily: '"Satoshi", sans-serif' }}
                  >
                    {t('settings.translatedLanguagesNote')}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel sx={{ fontFamily: '"Satoshi", sans-serif', fontSize: '0.8rem' }}>{t('settings.timeZone')}</InputLabel>
                    <Select
                      value={accountSettings.timeZone}
                      onChange={handleAccountSettingChange('timeZone')}
                      label={t('settings.timeZone')}
                      startAdornment={<AccessTime sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />}
                      sx={{ borderRadius: '8px', fontFamily: '"Satoshi", sans-serif', fontSize: '0.8rem' }}
                    >
                      {timeZones.map((tz) => (
                        <MenuItem key={tz.value} value={tz.value} sx={{ fontFamily: '"Satoshi", sans-serif', fontSize: '0.8rem' }}>
                          {tz.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel sx={{ fontFamily: '"Satoshi", sans-serif', fontSize: '0.8rem' }}>{t('settings.dateFormat')}</InputLabel>
                    <Select
                      value={accountSettings.dateFormat}
                      onChange={handleAccountSettingChange('dateFormat')}
                      label={t('settings.dateFormat')}
                      startAdornment={<CalendarToday sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />}
                      sx={{ borderRadius: '8px', fontFamily: '"Satoshi", sans-serif', fontSize: '0.8rem' }}
                    >
                      {dateFormats.map((format) => (
                        <MenuItem key={format.value} value={format.value} sx={{ fontFamily: '"Satoshi", sans-serif', fontSize: '0.8rem' }}>
                          {format.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label={t('settings.autoLogoutTimer')}
                    value={accountSettings.autoLogoutTimer}
                    onChange={handleAccountSettingChange('autoLogoutTimer')}
                    InputProps={{
                      startAdornment: <Timer sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />,
                      inputProps: { min: 5, max: 480 },
                      sx: { fontFamily: '"Satoshi", sans-serif', fontSize: '0.8rem' }
                    }}
                    InputLabelProps={{ sx: { fontFamily: '"Satoshi", sans-serif', fontSize: '0.8rem' } }}
                    helperText={t('settings.autoLogoutHelper')}
                    FormHelperTextProps={{ sx: { fontSize: '0.68rem', fontFamily: '"Satoshi", sans-serif', mt: 0.25 } }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1.5, borderColor }} />
                  <Box display="flex" alignItems="center" gap={1} mb={0.75}>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '5px',
                        bgcolor: colors.iconBg,
                        color: colors.iconColor,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Palette sx={{ fontSize: 14 }} />
                    </Box>
                    <Typography variant="subtitle2" sx={{ fontFamily: '"Satoshi", sans-serif', fontWeight: 700, fontSize: '0.85rem', color: textColorPrimary }}>
                      {t('settings.theme')}
                    </Typography>
                  </Box>
                  <FormControl component="fieldset">
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={accountSettings.theme === 'dark'}
                          onChange={(e) => {
                            const newTheme = e.target.checked ? 'dark' : 'light';
                            setAccountSettings({
                              ...accountSettings,
                              theme: newTheme,
                            });
                            setSharedThemeMode(newTheme);
                          }}
                        />
                      }
                      label={
                        <Typography sx={{ fontFamily: '"Satoshi", sans-serif', fontSize: '0.8rem', fontWeight: 600, color: textColorPrimary }}>
                          {accountSettings.theme === 'dark' ? t('settings.darkMode') : t('settings.lightMode')}
                        </Typography>
                      }
                    />
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', ml: 4.5, fontSize: '0.7rem', fontFamily: '"Satoshi", sans-serif' }}>
                      {t('settings.themeDescription')}
                    </Typography>
                  </FormControl>
                </Grid>
              </Grid>

              {/* MFA / Two-Factor Authentication */}
              <Divider sx={{ my: 2, borderColor }} />
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '5px',
                      bgcolor: colors.iconBg,
                      color: colors.iconColor,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Security sx={{ fontSize: 14 }} />
                  </Box>
                  <Typography variant="subtitle2" sx={{ fontFamily: '"Satoshi", sans-serif', fontWeight: 700, fontSize: '0.85rem', color: textColorPrimary }}>
                    Two-Factor Authentication (2FA)
                  </Typography>
                  {mfaEnabled && (
                    <Chip icon={<CheckCircle sx={{ fontSize: '13px !important' }} />} label="Enabled" color="success" size="small" sx={{ ml: 1, height: 20, fontSize: '0.7rem', fontWeight: 700 }} />
                  )}
                </Box>

                {mfaError && <Alert severity="error" sx={{ mb: 1.5, py: 0.25, borderRadius: '6px', fontSize: '0.75rem', fontFamily: '"Satoshi", sans-serif' }}>{mfaError}</Alert>}
                {mfaSuccess && <Alert severity="success" sx={{ mb: 1.5, py: 0.25, borderRadius: '6px', fontSize: '0.75rem', fontFamily: '"Satoshi", sans-serif' }}>{mfaSuccess}</Alert>}

                {!mfaEnabled && mfaSetupStep === 'idle' && (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', fontFamily: '"Satoshi", sans-serif', mb: 1.5 }}>
                      Use an authenticator app (Google Authenticator, Authy) to add an extra layer of security to your account.
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Lock sx={{ fontSize: 14 }} />}
                      onClick={handleMfaSetup}
                      disabled={mfaLoading}
                      sx={{
                        borderRadius: '6px',
                        borderColor: colors.primary,
                        color: colors.primary,
                        height: 30,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        textTransform: 'none',
                        fontFamily: '"Satoshi", sans-serif',
                        '&:hover': { borderColor: colors.btnHoverBg }
                      }}
                    >
                      {mfaLoading ? <CircularProgress size={14} /> : 'Enable 2FA'}
                    </Button>
                  </Box>
                )}

                {mfaSetupStep === 'verify' && (
                  <Box>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem', fontFamily: '"Satoshi", sans-serif', mb: 1 }}>
                      Scan this QR code with your authenticator app, then enter the 6-digit code below to confirm.
                    </Typography>
                    {mfaQrCode && (
                      <Box sx={{ my: 1.5 }}>
                        <img src={mfaQrCode} alt="MFA QR Code" style={{ width: 140, height: 140, display: 'block', borderRadius: 8 }} />
                      </Box>
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      Manual key: {mfaSecret}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                      <TextField
                        label="6-digit code"
                        value={mfaCode}
                        onChange={e => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        inputProps={{ maxLength: 6, inputMode: 'numeric', sx: { fontSize: '0.8rem', py: 0.75 } }}
                        size="small"
                        sx={{ width: 140, '& .MuiOutlinedInput-root': { borderRadius: '6px' } }}
                      />
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleMfaVerifySetup}
                        disabled={mfaLoading || mfaCode.length !== 6}
                        sx={{
                          borderRadius: '6px',
                          background: colors.btnBg,
                          height: 32,
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          textTransform: 'none',
                          boxShadow: 'none',
                          fontFamily: '"Satoshi", sans-serif',
                          '&:hover': { background: colors.btnHoverBg, boxShadow: 'none' }
                        }}
                      >
                        {mfaLoading ? <CircularProgress size={14} /> : 'Verify & Enable'}
                      </Button>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => { setMfaSetupStep('idle'); setMfaQrCode(''); setMfaSecret(''); setMfaCode(''); }}
                        sx={{ height: 32, fontSize: '0.75rem', textTransform: 'none', fontFamily: '"Satoshi", sans-serif' }}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </Box>
                )}

                {mfaEnabled && mfaSetupStep === 'idle' && (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', fontFamily: '"Satoshi", sans-serif', mb: 1.5 }}>
                      Your account is protected with two-factor authentication. Enter a code from your authenticator app to disable it.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                      <TextField
                        label="6-digit code"
                        value={mfaCode}
                        onChange={e => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        inputProps={{ maxLength: 6, inputMode: 'numeric', sx: { fontSize: '0.8rem', py: 0.75 } }}
                        size="small"
                        sx={{ width: 140, '& .MuiOutlinedInput-root': { borderRadius: '6px' } }}
                      />
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={handleMfaDisable}
                        disabled={mfaLoading || mfaCode.length !== 6}
                        sx={{ borderRadius: '6px', height: 32, fontSize: '0.75rem', fontWeight: 600, textTransform: 'none', fontFamily: '"Satoshi", sans-serif' }}
                      >
                        {mfaLoading ? <CircularProgress size={14} /> : 'Disable 2FA'}
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>

              <Box mt={2.5}>
                <Button
                  variant="contained"
                  startIcon={<Save sx={{ fontSize: 16 }} />}
                  onClick={handleSave}
                  disabled={loading}
                  sx={{
                    borderRadius: '6px',
                    background: colors.btnBg,
                    fontFamily: '"Satoshi", sans-serif',
                    fontWeight: 700,
                    fontSize: '0.8rem',
                    height: 32,
                    px: 2.5,
                    textTransform: 'none',
                    boxShadow: 'none',
                    '&:hover': { background: colors.btnHoverBg, boxShadow: 'none' },
                  }}
                >
                  {t('settings.saveChanges')}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default UserSettings;

